import { useRef, useState } from "react";
import Swal from "sweetalert2";
import AdminLayout from "../layout/AdminLayoutSewa";
import { adminUploadCompleteSewaData } from "../api";

function FilePicker({ inputRef, file, onChange }) {
  return (
    <div
      className="mt-5 border-2 border-dashed rounded-lg p-9 text-center cursor-pointer hover:border-blue-400 hover:bg-gray-50"
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={(event) => onChange(event.target.files?.[0] || null)}
      />
      {file ? (
        <div>
          <p className="font-medium text-green-700">{file.name}</p>
          <p className="text-sm text-gray-500 mt-1">Klik untuk tukar fail</p>
        </div>
      ) : (
        <div>
          <p className="font-medium text-gray-700">Klik untuk pilih fail Excel lengkap</p>
          <p className="text-sm text-gray-500 mt-1">.xlsx / .xls sahaja</p>
        </div>
      )}
    </div>
  );
}

function count(value) {
  return Number(value || 0).toLocaleString("en-MY");
}

export default function SewaBillsUploaderPage() {
  const [template, setTemplate] = useState("penjaja");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [recent, setRecent] = useState(null);
  const inputRef = useRef(null);

  const clearFile = () => {
    setFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file) {
      return Swal.fire({
        icon: "warning",
        title: "Tiada fail",
        text: "Sila pilih fail Excel lengkap.",
      });
    }

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Gantikan semua data?",
      html: `Fail ini akan menggantikan <b>unit, kawasan dan bills</b> untuk ${template}.`,
      showCancelButton: true,
      confirmButtonText: "Ya, muat naik",
      cancelButtonText: "Batal",
    });
    if (!confirmation.isConfirmed) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const result = await adminUploadCompleteSewaData(template, formData);

      setRecent({
        ...result,
        uploadedAt: new Date().toLocaleString("en-MY"),
        fileName: file.name,
        template,
      });
      clearFile();

      await Swal.fire({
        icon: "success",
        title: "Muat naik berjaya",
        html:
          `${count(result?.insertedCustomers)} unit, ` +
          `${count(result?.insertedKawasans)} kawasan dan ` +
          `${count(result?.insertedBills)} bills telah dikemas kini.`,
      });
    } catch (error) {
      const data = error?.response?.data;
      const details = data?.recognizedCustomers !== undefined
        ? `<br><small>Unit: ${count(data.recognizedCustomers)}, Kawasan: ${count(data.recognizedKawasans)}, Bills: ${count(data.recognizedBills)}</small>`
        : "";

      await Swal.fire({
        icon: "error",
        title: "Muat naik gagal",
        html: `${data?.error || "Fail tidak dapat dimuat naik."}${details}`,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto space-y-5">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Muat Naik Data Sewa</h2>
          <p className="text-sm text-gray-500 mt-1">
            Satu fail akan mengemas kini unit, kawasan dan bills serentak.
          </p>
        </div>

        {recent && (
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <div className="flex justify-between gap-3">
              <p className="font-semibold text-green-700">Muat naik terakhir berjaya</p>
              <p className="text-xs text-green-700">{recent.uploadedAt}</p>
            </div>
            <p className="text-sm mt-2">Fail: {recent.fileName}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              <div className="bg-white border rounded p-3">
                <p className="text-xs text-gray-500">Unit</p>
                <p className="text-lg font-semibold">{count(recent.insertedCustomers)}</p>
              </div>
              <div className="bg-white border rounded p-3">
                <p className="text-xs text-gray-500">Kawasan</p>
                <p className="text-lg font-semibold">{count(recent.insertedKawasans)}</p>
              </div>
              <div className="bg-white border rounded p-3">
                <p className="text-xs text-gray-500">Bills</p>
                <p className="text-lg font-semibold">{count(recent.insertedBills)}</p>
              </div>
            </div>
          </div>
        )}

        <section className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-bold text-gray-800">Fail Lengkap Unit, Kawasan & Bills</h3>
          <p className="text-sm text-gray-500 mt-1">
            Fail mesti mengandungi BIL, KOD KAWASAN, KAWASAN, nombor unit,
            nombor akaun, CAJ SEWA, BAYARAN SEWA dan TUNGGAKAN.
          </p>

          <label className="block text-xs text-gray-600 mt-4">Template</label>
          <select
            className="border rounded-md w-full p-2 text-sm bg-white mt-1"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
            disabled={uploading}
          >
            <option value="penjaja">Penjaja</option>
            <option value="perumahan">Perumahan</option>
          </select>

          <FilePicker inputRef={inputRef} file={file} onChange={setFile} />

          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 mt-4">
            Semua perubahan dibuat dalam satu transaksi. Jika mana-mana bahagian fail tidak sah,
            data sedia ada tidak akan diubah.
          </p>

          <div className="flex justify-between mt-5">
            <button
              className="py-2 px-5 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-50"
              onClick={clearFile}
              disabled={!file || uploading}
            >
              Clear
            </button>
            <button
              className="py-2 px-5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? "Uploading..." : "Upload Semua Data"}
            </button>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
}
